import { cn } from "@/lib/utils";
import { getProgressColor } from "@/lib/progress-status";

interface ProgressRingProps {
  value: number;
  size?: 28 | 36 | 44;
  thickness?: number;
  showLabel?: boolean;
  className?: string;
  ariaLabel?: string;
}

export function ProgressRing({
  value,
  size = 44,
  thickness,
  showLabel = true,
  className,
  ariaLabel,
}: ProgressRingProps) {
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  const stroke = thickness ?? Math.max(3, Math.round(size / 11));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;
  const fillColor = getProgressColor(clamped);

  return (
    <div
      className={cn("relative inline-grid place-items-center", className)}
      style={{ width: size, height: size }}
      role="img"
      aria-label={ariaLabel ?? `${clamped}% complete`}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{ transform: "rotate(-90deg)" }}
        aria-hidden="true"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="hsl(var(--border))"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={fillColor}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 320ms ease" }}
        />
      </svg>
      {showLabel ? (
        <span
          className="absolute inset-0 grid place-items-center text-[11px] font-bold tabular-nums tracking-tight text-[hsl(var(--foreground))]"
        >
          {clamped}%
        </span>
      ) : null}
    </div>
  );
}
